type Listener = (payload: unknown) => void;

export class WsClient {
  private ws: WebSocket | null = null;
  private reconnectDelay = 1000;
  private rooms = new Set<string>();
  private listeners = new Map<string, Set<Listener>>();
  private closed = false;

  constructor(private readonly url: string) {}

  connect(): void {
    if (this.closed) return;
    this.ws = new WebSocket(this.url);
    this.ws.addEventListener('open', () => {
      this.reconnectDelay = 1000;
      for (const room of this.rooms) {
        this.ws?.send(JSON.stringify({ type: 'join', room }));
      }
    });
    this.ws.addEventListener('message', (e) => {
      try {
        const parsed = JSON.parse(e.data) as { room: string; payload: unknown };
        const set = this.listeners.get(parsed.room);
        if (!set) return;
        for (const fn of set) fn(parsed.payload);
      } catch {
        // ignore
      }
    });
    this.ws.addEventListener('close', () => {
      if (this.closed) return;
      setTimeout(() => this.connect(), this.reconnectDelay);
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30_000);
    });
  }

  join(room: string, listener: Listener): () => void {
    if (!this.rooms.has(room)) {
      this.rooms.add(room);
      this.ws?.send(JSON.stringify({ type: 'join', room }));
    }
    let set = this.listeners.get(room);
    if (!set) {
      set = new Set();
      this.listeners.set(room, set);
    }
    set.add(listener);
    return () => {
      set!.delete(listener);
      if (set!.size === 0) {
        this.listeners.delete(room);
        this.rooms.delete(room);
        this.ws?.send(JSON.stringify({ type: 'leave', room }));
      }
    };
  }

  close(): void {
    this.closed = true;
    this.ws?.close();
  }
}

export const wsClient = new WsClient(
  `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`,
);
